import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "@athena/ui";
import { ConfirmDialog } from "@/components/ConfirmDialog";
import { useReturnToOutreach } from "@/hooks/useOutreachStatus";
import { fullName, type ClientRecord } from "../helpers/client";

interface ReturnToOutreachProps {
    client: ClientRecord;
}

/**
 * The way back from lost contact, on the profile only: the client goes back into outreach and the
 * reason they were marked lost is cleared. Not offered for do not contact, which is usually the
 * client's own request and not a coordinator's to undo with a button.
 */
export function ReturnToOutreach({ client }: ReturnToOutreachProps) {
    const { t } = useTranslation();
    const returnToOutreach = useReturnToOutreach();
    const [
        open,
        setOpen,
    ] = useState<boolean>(false);
    const [
        saving,
        setSaving,
    ] = useState<boolean>(false);

    const name = fullName(client);

    const confirm = async () => {
        setSaving(true);
        try {
            await returnToOutreach(client.id, name);
            setOpen(false);
        } catch {
            // The hook has said what went wrong; the confirmation stays up so it can be tried again.
        } finally {
            setSaving(false);
        }
    };

    return (
        <>
            <Button variant="outline" className="w-auto" onClick={() => setOpen(true)}>
                {t("admin.alumni.returnToOutreach.action")}
            </Button>
            <ConfirmDialog
                open={open}
                onOpenChange={(next) => {
                    if (!saving) setOpen(next);
                }}
                title={t("admin.alumni.returnToOutreach.title", { name })}
                description={t("admin.alumni.returnToOutreach.description", { firstName: client.firstName })}
                confirmLabel={t("admin.alumni.returnToOutreach.action")}
                loading={saving}
                onConfirm={confirm}
            />
        </>
    );
}
